import type { WalletErrorCode } from "../../shared/marketWalletErrorDetails";

const codes: readonly string[] = ["wallet_rejected", "wallet_request_expired", "wallet_disconnected", "wallet_network_error", "wallet_unsupported", "wallet_invalid_request", "wallet_insufficient_balance", "wallet_unknown_error"];
type Failure = { id: string; code: WalletErrorCode; expiresAt: number };
/** Short-lived display hints, never payment evidence or authority to refund. */
export class WalletFailureMemory {
  readonly #key: string;
  #rows: Failure[] = [];
  readonly #storage: Pick<Storage, "getItem" | "setItem"> | undefined;
  constructor(userId: string, private readonly now: () => number, storage?: Pick<Storage, "getItem" | "setItem">) {
    this.#key = `diamond-tapper:wallet-failure:v1:${encodeURIComponent(userId)}`;
    try {
      this.#storage = storage ?? window.localStorage;
      const saved: unknown = JSON.parse(this.#storage.getItem(this.#key) ?? "[]");
      if (Array.isArray(saved)) this.#rows = saved.slice(-20).filter((value: unknown): value is Failure => {
        if (!value || typeof value !== "object") return false;
        const row = value as Record<string, unknown>;
        return typeof row.id === "string" && /^[\w:-]{1,100}$/.test(row.id) && typeof row.code === "string" && codes.includes(row.code)
          && typeof row.expiresAt === "number" && Number.isFinite(row.expiresAt) && row.expiresAt > now() && row.expiresAt <= now() + 86400000;
      }).map(({ id, code, expiresAt }) => ({ id, code, expiresAt }));
    } catch { /* Inaccessible storage cannot block a wallet. */ }
  }
  get(id: string): WalletErrorCode | undefined { return this.#rows.find(row => row.id === id && row.expiresAt > this.now())?.code; }
  set(id: string, code: WalletErrorCode, expiresAt: number): void {
    this.clear(id);
    if (/^[\w:-]{1,100}$/.test(id) && codes.includes(code) && expiresAt > this.now()) this.#rows.push({ id, code, expiresAt: Math.min(expiresAt, this.now() + 86400000) });
    this.#persist();
  }
  clear(id: string): void { this.#rows = this.#rows.filter(row => row.id !== id && row.expiresAt > this.now()); this.#persist(); }
  #persist(): void {
    this.#rows = this.#rows.filter(row => row.expiresAt > this.now()).slice(-20);
    try { this.#storage?.setItem(this.#key, JSON.stringify(this.#rows)); } catch { /* Display only. */ }
  }
}
